import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { format, subDays, addDays } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  Store, Calendar, RefreshCw, Filter, ChevronLeft, ChevronRight,
  AlertTriangle, CheckCircle2, XCircle, Clock, Info, Banknote,
  CreditCard, Wallet, ExternalLink, TrendingUp, TrendingDown
} from 'lucide-react';
import { StorePeriodFilter, useStorePeriodFilter } from '@/components/retail/StorePeriodFilter';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Format currency
const formatCurrency = (value, showSign = false) => {
  if (value === null || value === undefined) return '—';
  const formatted = new Intl.NumberFormat('pt-PT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(Math.abs(value));
  
  if (showSign && value !== 0) {
    return value > 0 ? `+${formatted}` : `-${formatted}`;
  }
  return formatted;
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    NO_DATA: { label: 'Sem Dados', color: 'bg-slate-100 text-slate-600', icon: Info },
    OPEN: { label: 'Aberto', color: 'bg-blue-100 text-blue-700', icon: Clock },
    READY_TO_CLOSE: { label: 'Pronto', color: 'bg-amber-100 text-amber-700', icon: Clock },
    CLOSED_OK: { label: 'Fechado OK', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    CLOSED_WITH_DIFFERENCE: { label: 'Com Diferença', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
    REOPENED: { label: 'Reaberto', color: 'bg-purple-100 text-purple-700', icon: Clock },
    POST_CLOSE_DATA_CHANGE: { label: 'Dados Alterados', color: 'bg-red-100 text-red-700', icon: XCircle },
  };
  
  const cfg = config[status] || config.NO_DATA;
  const Icon = cfg.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// Difference cell with color coding
const DifferenceCell = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
  
  const isZero = Math.abs(value) < 0.01;
  const isNegative = value < 0;
  
  return (
    <span className={`font-medium ${isZero ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-amber-600'}`}>
      {isZero ? '0,00 €' : formatCurrency(value, true)}
    </span>
  );
};

// Outstanding indicator
const OutstandingIndicator = ({ value, days }) => {
  if (value === null || value === undefined || value === 0) {
    return <span className="text-emerald-600 font-medium">0,00 €</span>;
  }
  
  const isLate = days > 3;
  
  return (
    <div className={`${isLate ? 'text-red-600' : 'text-amber-600'}`}>
      <span className="font-medium">{formatCurrency(value)}</span>
      {days > 0 && (
        <span className="text-xs ml-1">({days}d)</span>
      )}
    </div>
  );
};

export default function RetailControleDiario() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Store name mapping hook
  const { getStoreName } = useStoreMapping();
  
  // Use shared filter hook - default to 'this_month' for all retail pages
  const {
    selectedStore,
    setSelectedStore,
    period,
    setPeriod,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    startDate,
    endDate,
    isSingleDay,
    filterProps,
  } = useStorePeriodFilter({ defaultPeriod: 'this_month', defaultStore: '' });
  
  // State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [stores, setStores] = useState([]);
  
  // Page-specific filters
  const [showOnlyWithDifference, setShowOnlyWithDifference] = useState(false);
  const [showOnlyWithOutstanding, setShowOnlyWithOutstanding] = useState(false);
  const [showOnlyPendingClose, setShowOnlyPendingClose] = useState(false);
  const [showOnlyDataChange, setShowOnlyDataChange] = useState(false);
  
  // Load stores
  useEffect(() => {
    const loadStores = async () => {
      try {
        const res = await axios.get(`${API}/retail-analytics/stores`);
        setStores(res.data.stores || []);
      } catch (err) {
        console.error('Error loading stores:', err);
      }
    };
    loadStores();
  }, []);
  
  // Load control data
  const loadData = async () => {
    setLoading(true);
    
    try {
      // For single day, load financial control summary
      // For range, load summary per day
      const params = new URLSearchParams();
      params.set('business_date', startDate);
      if (startDate !== endDate) {
        params.set('end_date', endDate);
      }
      if (selectedStore) {
        params.set('store_code', selectedStore);
      }
      
      const res = await axios.get(`${API}/retail-closures/financial-control?${params.toString()}`);
      
      let results = res.data || [];
      
      // Load closure details for each store
      const enrichedData = await Promise.all(results.map(async (item) => {
        try {
          // For multi-day, use the item's business_date; for single day, use startDate
          const dateToFetch = item.business_date || startDate;
          const closureRes = await axios.get(`${API}/retail-closures/closures/${item.store_code}/${dateToFetch}`);
          const outstandingRes = await axios.get(`${API}/retail-closures/outstanding/${item.store_code}`);
          
          return {
            ...item,
            closure: closureRes.data,
            outstanding: outstandingRes.data,
          };
        } catch (err) {
          return item;
        }
      }));
      
      setData(enrichedData);
    } catch (err) {
      console.error('Error loading control data:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (stores.length > 0) {
      loadData();
    }
  }, [selectedStore, startDate, endDate, stores]);
  
  const handleRefresh = () => {
    loadData();
  };
  
  // Quick day navigation for single-day mode
  const goToPreviousDay = () => {
    const prev = subDays(new Date(startDate), 1);
    setPeriod('custom');
    const newDate = format(prev, 'yyyy-MM-dd');
    setCustomStartDate(newDate);
    setCustomEndDate(newDate);
  };
  
  const goToNextDay = () => {
    const next = addDays(new Date(startDate), 1);
    setPeriod('custom');
    const newDate = format(next, 'yyyy-MM-dd');
    setCustomStartDate(newDate);
    setCustomEndDate(newDate);
  };
  
  const goToToday = () => {
    setPeriod('today');
  };
  
  // Filtered data
  const filteredData = useMemo(() => {
    let result = data;
    
    if (showOnlyWithDifference) {
      result = result.filter(r => {
        const cashDiff = r.closure?.differences?.cash_difference;
        const cardDiff = r.closure?.differences?.card_difference;
        return (cashDiff !== null && cashDiff !== 0) || (cardDiff !== null && cardDiff !== 0);
      });
    }
    
    if (showOnlyWithOutstanding) {
      result = result.filter(r => (r.outstanding?.outstanding_balance || 0) > 0);
    }
    
    if (showOnlyPendingClose) {
      result = result.filter(r => {
        const status = r.closure?.status;
        return status && !['CLOSED_OK', 'CLOSED_WITH_DIFFERENCE'].includes(status);
      });
    }
    
    if (showOnlyDataChange) {
      result = result.filter(r => r.closure?.status === 'POST_CLOSE_DATA_CHANGE');
    }
    
    return result;
  }, [data, showOnlyWithDifference, showOnlyWithOutstanding, showOnlyPendingClose, showOnlyDataChange]);
  
  // Navigate to closure
  const goToClosure = (storeCode) => {
    navigate(`/lojas/fecho?store=${storeCode}&period=custom&start=${startDate}&end=${startDate}`);
  };
  
  // Navigate to deposits
  const goToDeposits = (storeCode) => {
    navigate(`/lojas/depositos?store=${storeCode}&period=this_month`);
  };
  
  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="controle-diario-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Controlo Diário
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Visão operacional de fechos de caixa e depósitos
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Shared Store/Period Filter */}
            <StorePeriodFilter
              {...filterProps}
              onRefresh={handleRefresh}
              loading={loading}
              showAllStores={true}
            />
            
            {/* Quick day navigation for single-day mode */}
            {isSingleDay && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={goToPreviousDay}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={goToNextDay}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={goToToday} className="ml-1">
                  Hoje
                </Button>
              </div>
            )}
            
            {/* Page-specific quick filters */}
            <div className="flex items-center gap-4 ml-auto flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={showOnlyWithDifference}
                  onCheckedChange={setShowOnlyWithDifference}
                  data-testid="filter-difference"
                />
                <span>Com Diferença</span>
              </label>
              
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={showOnlyWithOutstanding}
                  onCheckedChange={setShowOnlyWithOutstanding}
                  data-testid="filter-outstanding"
                />
                <span>Saldo Pendente</span>
              </label>
              
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={showOnlyPendingClose}
                  onCheckedChange={setShowOnlyPendingClose}
                  data-testid="filter-pending"
                />
                <span>Por Fechar</span>
              </label>
              
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={showOnlyDataChange}
                  onCheckedChange={setShowOnlyDataChange}
                  data-testid="filter-data-change"
                />
                <span className="text-red-600">Dados Alterados</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Results */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {isSingleDay 
              ? (startDate === format(new Date(), 'yyyy-MM-dd') 
                  ? 'Hoje' 
                  : format(new Date(startDate), "EEEE, d 'de' MMMM", { locale: pt }))
              : `${format(new Date(startDate), 'dd/MM/yyyy')} a ${format(new Date(endDate), 'dd/MM/yyyy')}`
            }
            <span className="ml-2 text-slate-400 font-normal">({filteredData.length} lojas)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Store className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nenhum dado encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10">Loja</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <Banknote className="w-3 h-3 text-emerald-600" />
                        Esperado
                      </span>
                    </TableHead>
                    <TableHead className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <Banknote className="w-3 h-3 text-emerald-600" />
                        Contado
                      </span>
                    </TableHead>
                    <TableHead className="text-right">Dif. Cash</TableHead>
                    <TableHead className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <CreditCard className="w-3 h-3 text-blue-600" />
                        Esperado
                      </span>
                    </TableHead>
                    <TableHead className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <CreditCard className="w-3 h-3 text-blue-600" />
                        TPA
                      </span>
                    </TableHead>
                    <TableHead className="text-right">Dif. Card</TableHead>
                    <TableHead className="text-right">Float</TableHead>
                    <TableHead className="text-right">Depositável</TableHead>
                    <TableHead className="text-right">Depositado</TableHead>
                    <TableHead className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <Wallet className="w-3 h-3" />
                        Por Depositar
                      </span>
                    </TableHead>
                    <TableHead className="w-[100px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((row) => {
                    const closure = row.closure || {};
                    const outstanding = row.outstanding || {};
                    const expected = closure.expected || {};
                    const declared = closure.declared || {};
                    const differences = closure.differences || {};
                    
                    return (
                      <TableRow 
                        key={row.store_code}
                        className={closure.status === 'POST_CLOSE_DATA_CHANGE' ? 'bg-red-50' : ''}
                      >
                        <TableCell className="font-medium sticky left-0 bg-white z-10">
                          {getStoreName(row.store_code)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={closure.status} />
                          {closure.payment_reclassification && (
                            <div className="text-[10px] text-emerald-700 mt-0.5" data-testid={`controle-payment-corrected-${closure.store_code}`}
                              title={`WinTouch: numerário ${formatCurrency(closure.payment_reclassification.wintouch_cash)} · MB ${formatCurrency(closure.payment_reclassification.wintouch_card)}`}>
                              Pagamento corrigido
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(expected.cash)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(declared.counted_cash)}
                        </TableCell>
                        <TableCell className="text-right">
                          <DifferenceCell value={differences.cash_difference} />
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(expected.card)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(declared.tpa_closing_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          <DifferenceCell value={differences.card_difference} />
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-slate-500">
                          {formatCurrency(closure.cash_float_retained)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(closure.depositable_cash)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-emerald-600">
                          {formatCurrency(outstanding.total_allocated)}
                        </TableCell>
                        <TableCell className="text-right">
                          <OutstandingIndicator 
                            value={outstanding.outstanding_balance} 
                            days={outstanding.days_pending}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => goToClosure(row.store_code)}
                              title="Ir para Fecho"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            {(outstanding.outstanding_balance || 0) > 0 && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => goToDeposits(row.store_code)}
                                title="Ir para Depósitos"
                                className="text-amber-600"
                              >
                                <Wallet className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Summary cards */}
      {!loading && filteredData.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase">Total Lojas</p>
              <p className="text-2xl font-bold">{filteredData.length}</p>
            </CardContent>
          </Card>
          
          <Card className={filteredData.filter(r => r.closure?.status === 'CLOSED_OK').length === filteredData.length ? 'border-emerald-200' : ''}>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase">Fechadas OK</p>
              <p className="text-2xl font-bold text-emerald-600">
                {filteredData.filter(r => r.closure?.status === 'CLOSED_OK').length}
              </p>
            </CardContent>
          </Card>
          
          <Card className={filteredData.some(r => r.closure?.status === 'CLOSED_WITH_DIFFERENCE') ? 'border-orange-200' : ''}>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase">Com Diferença</p>
              <p className="text-2xl font-bold text-orange-600">
                {filteredData.filter(r => r.closure?.status === 'CLOSED_WITH_DIFFERENCE').length}
              </p>
            </CardContent>
          </Card>
          
          <Card className={filteredData.some(r => (r.outstanding?.outstanding_balance || 0) > 0) ? 'border-amber-200' : ''}>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500 uppercase">Total Pendente</p>
              <p className="text-2xl font-bold text-amber-600">
                {formatCurrency(filteredData.reduce((sum, r) => sum + (r.outstanding?.outstanding_balance || 0), 0))}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </div>
  );
}
